import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, WEB_URL } from "../App";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import BottomNav from "../components/BottomNav";
import { 
  Wine, MapPin, Clock, Users, QrCode, DollarSign, 
  ExternalLink, Copy, Plus, Settings, Calendar, ChevronRight,
  Star, Zap, Trophy, Tag, Shield
} from "lucide-react";
import { toast } from "sonner";

// Badge milestone definitions
const BADGE_MILESTONES = [
  { id: "rising_star", name: "Rising Star", threshold: 25, icon: Star, color: "#FFD700" },
  { id: "rockstar", name: "RockStar", threshold: 100, icon: Zap, color: "#FF6B35" },
  { id: "local_legend", name: "Local Legend", threshold: 200, icon: Trophy, color: "#9333EA" }
];

const BartenderDashboard = () => {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const [showQR, setShowQR] = useState(false);
  const [stats, setStats] = useState({ followers: 0, following: 0 });
  const [invites, setInvites] = useState([]);

  const profileUrl = `${WEB_URL}/b/${user?.username}`;

  // Helper functions to construct payment URLs from usernames
  const getVenmoUrl = (username) => {
    if (!username) return null;
    const cleanUsername = username.replace('@', '');
    return `https://venmo.com/u/${cleanUsername}`;
  };

  const getCashAppUrl = (username) => {
    if (!username) return null;
    const cleanUsername = username.replace('$', '');
    return `https://cash.app/$${cleanUsername}`;
  };

  const getPayPalUrl = (username) => {
    if (!username) return null;
    return `https://paypal.me/${username}`;
  };

  useEffect(() => {
    fetchStats();
    fetchInvites();
  }, []);

  const fetchStats = async () => {
    try {
      const [followersRes, followingRes, followingVenuesRes] = await Promise.all([
        axios.get(`${API}/followers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/following`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/following-venues`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const followingUsersCount = followingRes.data.length;
      const followingVenuesCount = followingVenuesRes.data.length;
      const totalFollowing = followingUsersCount + followingVenuesCount;
      
      setStats({ 
        followers: followersRes.data.length,
        following: totalFollowing
      });
    } catch (e) {
      console.error("Error fetching stats:", e);
    }
  };

  const fetchInvites = async () => {
    try {
      const invitesRes = await axios.get(`${API}/invites`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setInvites(invitesRes.data.slice(0, 3));
    } catch (e) {
      console.error("Error fetching invites:", e);
    }
  };

  const copyProfileLink = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success("Profile link copied!");
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    return `${API}/files/${path}?auth=${token}`;
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  };

  // Get the next badge milestone based on current follower count
  const getNextMilestone = () => {
    const followers = stats.followers;
    for (const milestone of BADGE_MILESTONES) {
      if (followers < milestone.threshold) {
        return milestone;
      }
    }
    // All milestones achieved
    return null;
  };

  // Get progress percentage to next milestone
  const getMilestoneProgress = () => {
    const followers = stats.followers;
    const nextMilestone = getNextMilestone();
    
    if (!nextMilestone) return 100;
    
    // Find previous milestone threshold
    const currentIndex = BADGE_MILESTONES.findIndex(m => m.id === nextMilestone.id);
    const prevThreshold = currentIndex > 0 ? BADGE_MILESTONES[currentIndex - 1].threshold : 0;
    
    const progress = ((followers - prevThreshold) / (nextMilestone.threshold - prevThreshold)) * 100;
    return Math.min(Math.max(progress, 0), 100);
  };

  if (!user) return null;

  const nextMilestone = getNextMilestone();
  const milestoneProgress = getMilestoneProgress();
  const followersToNext = nextMilestone ? nextMilestone.threshold - stats.followers : 0;

  return (
    <div className="min-h-screen bg-background overflow-y-auto" data-testid="bartender-dashboard">
      {/* Header */}
      <header className="p-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Wine className="w-6 h-6 text-primary" />
          <span className="text-xl font-bold text-white">PourCircle</span>
        </div>
        <div className="flex items-center gap-2">
          {user.is_admin && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate("/admin")}
              className="border-primary/50 text-primary hover:bg-primary/10 text-xs"
              data-testid="admin-btn"
            >
              <Shield className="w-4 h-4 mr-1" />
              ADMIN
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/edit-profile")}
            className="text-white/60 hover:text-white"
            data-testid="settings-btn"
          >
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="px-6 space-y-6 pb-24">
        {/* Profile Card */}
        <div className="glass-card p-6">
          <div className="flex items-start gap-4">
            <Avatar className="w-20 h-20 border-2 border-primary">
              <AvatarImage src={getImageUrl(user.profile_image)} />
              <AvatarFallback className="bg-primary/20 text-primary text-xl">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                {user.name}
              </h1>
              <p className="text-white/60">@{user.username}</p>
              {user.bio && <p className="text-white/80 mt-2 text-sm">{user.bio}</p>}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-3 gap-2 mt-6">
            <Button 
              onClick={() => navigate(`/b/${user.username}`)}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/5 text-xs w-full"
              data-testid="view-profile-btn"
            >
              <Users className="w-3 h-3 mr-1" />
              View Profile
            </Button>
            <Button 
              onClick={() => setShowQR(!showQR)}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/5 text-xs w-full"
              data-testid="qr-toggle-btn"
            >
              <QrCode className="w-3 h-3 mr-1" />
              QR Code
            </Button>
            <Button 
              onClick={copyProfileLink}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/5 text-xs w-full"
              data-testid="copy-link-btn"
            >
              <Copy className="w-3 h-3 mr-1" />
              Share
            </Button>
          </div>

          {/* QR Code */}
          {showQR && (
            <div className="mt-6 flex justify-center animate-fade-in">
              <div className="qr-container">
                <QRCodeSVG 
                  value={profileUrl} 
                  size={180}
                  level="H"
                  includeMargin={false}
                />
              </div>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <button 
            onClick={() => navigate("/followers")}
            className="glass-card-hover p-6 text-left"
            data-testid="followers-card"
          >
            <Users className="w-8 h-8 text-primary mb-3" />
            <p className="text-3xl font-bold text-white">{stats.followers}</p>
            <p className="text-white/60 text-sm">Followers</p>
          </button>
          <button 
            onClick={() => navigate("/followers?tab=following")}
            className="glass-card-hover p-6 text-left"
            data-testid="following-card"
          >
            <Users className="w-8 h-8 text-secondary mb-3" />
            <p className="text-3xl font-bold text-white">{stats.following}</p>
            <p className="text-white/60 text-sm">Following</p>
          </button>
        </div>

        {/* Badge Milestone Progress Card */}
        <div className="glass-card p-6" data-testid="milestone-progress">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Your Growth</h3>
            {nextMilestone && (
              <div className="flex items-center gap-1.5" style={{ color: nextMilestone.color }}>
                <nextMilestone.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{nextMilestone.name}</span>
              </div>
            )}
          </div>
          
          {nextMilestone ? (
            <>
              {/* Progress Bar */}
              <div className="relative h-3 bg-white/10 rounded-full overflow-hidden mb-3">
                <div 
                  className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${milestoneProgress}%`,
                    backgroundColor: nextMilestone.color
                  }}
                />
              </div>
              
              {/* Progress Text */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/60">{stats.followers} followers</span>
                <span className="text-white/80">
                  <span className="font-semibold" style={{ color: nextMilestone.color }}>{followersToNext}</span> to go!
                </span>
              </div>
              
              {/* Motivation Text */}
              <p className="text-white/50 text-xs mt-3">
                Reach {nextMilestone.threshold} followers to earn your {nextMilestone.name} badge!
              </p>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="flex justify-center gap-2 mb-2">
                {BADGE_MILESTONES.map(m => (
                  <m.icon key={m.id} className="w-6 h-6" style={{ color: m.color }} />
                ))}
              </div>
              <p className="text-white font-medium">All milestones achieved!</p>
              <p className="text-white/50 text-sm">You're a true PourCircle legend</p>
            </div>
          )}
        </div>

        {/* Exclusive Discounts Card */}
        <button 
          onClick={() => navigate("/discounts")}
          className="glass-card-hover p-6 w-full text-left"
          data-testid="discounts-btn"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center">
              <Tag className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium">Exclusive Discounts</p>
              <p className="text-white/50 text-sm">Special offers for bartenders</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/40" />
          </div>
        </button>

        {/* Invites Card */}
        <button 
          onClick={() => navigate("/invites")}
          className="glass-card-hover p-6 w-full text-left"
          data-testid="invites-btn"
        >
          <div className="flex items-center gap-4">
            <Calendar className="w-8 h-8 text-primary" />
            <div>
              <p className="text-white font-medium">Invites</p>
              <p className="text-white/50 text-sm">Plan meetups</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/40 ml-auto" />
          </div>
        </button>

        {/* Upcoming Invites */}
        {invites.length > 0 && (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-secondary" />
                Upcoming Invites
              </h2>
              <button 
                onClick={() => navigate("/invites")}
                className="text-primary text-sm flex items-center gap-1 hover:underline"
              >
                View All <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              {invites.map((invite) => (
                <div key={invite.id} className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-medium">{invite.location_name}</p>
                      <p className="text-white/60 text-sm">{invite.datetime_str}</p>
                    </div>
                    <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                      {invite.creator_id === user.id ? "YOUR INVITE" : "INVITED"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Links */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            Tip Links
          </h2>
          <div className="space-y-3">
            {user.venmo_link ? (
              <a 
                href={getVenmoUrl(user.venmo_link)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <span className="text-white">Venmo</span>
                <ExternalLink className="w-4 h-4 text-white/60" />
              </a>
            ) : null}
            {user.cashapp_link ? (
              <a 
                href={getCashAppUrl(user.cashapp_link)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <span className="text-white">Cash App</span>
                <ExternalLink className="w-4 h-4 text-white/60" />
              </a>
            ) : null}
            {user.paypal_link ? (
              <a 
                href={getPayPalUrl(user.paypal_link)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <span className="text-white">PayPal</span>
                <ExternalLink className="w-4 h-4 text-white/60" />
              </a>
            ) : null}
            {!user.venmo_link && !user.cashapp_link && !user.paypal_link && (
              <Button 
                onClick={() => navigate("/edit-profile")}
                variant="outline"
                className="w-full border-primary/50 text-primary hover:bg-primary/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Payment Links
              </Button>
            )}
          </div>
        </div>

        {/* Work Locations */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Work Locations
          </h2>
          {user.work_locations && user.work_locations.length > 0 ? (
            <div className="space-y-4">
              {user.work_locations.map((loc, i) => (
                <div key={i} className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <h3 className="text-white font-medium">{loc.name}</h3>
                  <p className="text-white/60 text-sm mt-1">{loc.address}</p>
                  {loc.schedule && loc.schedule.length > 0 && (
                    <div className="mt-3 flex items-center gap-2 text-white/60 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>{loc.schedule.length} shifts scheduled</span>
                    </div>
                  )}
                  {loc.happy_hours && loc.happy_hours.length > 0 && (
                    <span className="inline-block mt-2 px-2 py-1 bg-secondary/20 text-secondary text-xs rounded font-accent">
                      Happy Hour
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-white/40 mb-4">No work locations added</p>
              <Button 
                onClick={() => navigate("/edit-profile")}
                variant="outline"
                className="border-primary/50 text-primary hover:bg-primary/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </div>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default BartenderDashboard;
